import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";
import LeftNavbar from "../global/LeftNavbar";
import DataTable from "react-data-table-component";
import { RotatingLines } from "react-loader-spinner";
import { downloadFile, getAxiosResponse, handleError } from "../../services/authApiRoutes/APImethods";
import APIUrls from "../../services/APIUrls";
import FinanceFilter from "./FinanceFilter";
import FinanceAdvanceFilter from "./FinanceAdvanceFilter";
import Constants from "../../Constants";
import { AdvanceSearchParamsIcon } from "../../AppIcons";
import { humanize } from "../../services/Util";
import {useLocation } from "react-router-dom";
import { ConditionalBlock } from "../../ConditionalBlock";
import dateUtil from "../../services/DateUtil";


export default function Reports() {

    const [globalOverlay,setGlobalOverlay] = useState(false);
    const [projects, setProjects] = useState([]);
    const [showAdvanceFilter,setShowAdvanceFilter] = useState(false);
    const [searchAdFilters, updateSearchAdFilters] = useState({});
    const [dateFilter, setDateFilter] = useState();
    const [accountNameFilter, setAccountNameFilter] = useState();
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortParam, setSortParam] = useState();
    const {state} = useLocation();
    const retainFilter = state?.retainFilter;

    const buildQueryParams = (filters) => {
      let queryParams = new URLSearchParams();
      const { accountNameFilter, dateFilter, searchAdFilters, currentPage, rowsPerPage, sortParam } = filters;
  
      if (accountNameFilter) {
          queryParams.append('accountId', accountNameFilter);
      }
      if (dateFilter) {
        let startDate = dateUtil.getPSTStartOfDay(dateFilter[0]);
        let endDate = dateUtil.getPSTEndOfDay(dateFilter[1]);
        queryParams.append('startDate', startDate);
        queryParams.append('endDate', endDate);
      }
      if (searchAdFilters?.Project_ID) {
          queryParams.append('projectId', searchAdFilters.Project_ID);
      }

      if(searchAdFilters?.Project_Status) {
        queryParams.append('projectStatus', searchAdFilters.Project_Status.value);
      }
      
      if (searchAdFilters?.Subscriber_Invoice_Status) queryParams.append('subscriberInvoiceStatus', searchAdFilters.Subscriber_Invoice_Status.value);
      if (searchAdFilters?.Service_Provider_Invoice_Status) queryParams.append('serviceProviderInvoiceStatus', searchAdFilters.Service_Provider_Invoice_Status.value);
      if (searchAdFilters?.Service_Provider) queryParams.append('serviceProvider', searchAdFilters.Service_Provider.value);
      if (searchAdFilters?.Departments) queryParams.append('deptId', searchAdFilters.Departments.value);
      if (searchAdFilters?.Project_Owner) queryParams.append('projectOwner', searchAdFilters.Project_Owner.value);
      if (searchAdFilters?.Po_Number) queryParams.append('poNumber', searchAdFilters.Po_Number.value);
      if (searchAdFilters?.Subscriber_Invoice_Number) queryParams.append('subscriberInvoiceNumber', searchAdFilters.Subscriber_Invoice_Number.label);
      if (searchAdFilters?.Service_Provider_Invoice_Number) queryParams.append('serviceProviderInvoiceNumber', searchAdFilters.Service_Provider_Invoice_Number.label);
  
      if (currentPage !== undefined) {
          queryParams.append('pageNumber', currentPage);
      }
      if (rowsPerPage !== undefined) {
          queryParams.append('rowsPerPage', rowsPerPage);
      }
      if (sortParam) {
          const sortParams = new URLSearchParams(sortParam);
          sortParams.forEach((value, key) => {
              queryParams.append(key, value);
          });
      }
  
      return queryParams;
  };

    useEffect(() => {
        const fetchReports = async () => {
          setGlobalOverlay(true);
          try {
              const filters = { accountNameFilter, dateFilter, searchAdFilters, currentPage, rowsPerPage, sortParam };
              const queryParams = buildQueryParams(filters);
              const queryString = queryParams.toString();
              const res = await getAxiosResponse(`${APIUrls.REPORT_LIST_FINANCE}?${queryString}`);
              if (res?.data?.code === 200) {
                  setProjects(res.data.data.projectData);
                  setTotalRows(res.data.data.projectCount);
              }
          } catch (err) {
              handleError(err);
          } finally {
              setGlobalOverlay(false);
          }
        };
      
        fetchReports();
    }, [accountNameFilter, dateFilter, searchAdFilters, currentPage, rowsPerPage, sortParam]);

    useEffect(() => {
        document.body.classList.toggle("modal-open", showAdvanceFilter);
    },[showAdvanceFilter]);

    const handlePageChange = (paging) => {
        setCurrentPage(paging);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        if (!projects.length) return;
        setRowsPerPage(newRowsPerPage);
    };

    const columns = useMemo(() => [
        { id: "accountName", name: "Account Name", selector: row => row.customerName, width: '150px' },
        { id: "serviceProvider", name: "Service Provider", selector: row => row.vendorName, width: '150px' },
        { id: "createdDate", name: "Created Date", selector: row => row.createdDate.date, width: '150px', sortable: true },
        { id: "projectId", name: "Project Id", selector: row => row.projectId, width: '150px', sortable: true },
        { id: "projectName", name: "Project Name", selector: row => row.projectName, width: '150px'},
        { id: "projectStatus", name: "Project Status", selector: row => row.status.display, width: '150px'},
        { id: "closedOn", name: "Project Close Date", selector: row => (row.status.code === Constants.PROJECT_STATUS.closed ? row.closeDate.date : row.billingInclusionDate.date), width: '150px', sortable: true },
        { id: "serviceProviderProjectValue", name: "Service Provider Project Value", selector: row => row.vendorAmount.displayWithDecimals, width: '150px' },
        { id: "subscriberInvoiceStatus", name: "Subscriber Invoice Status", selector: row => row.customerBillingStatus, width: '150px' },
        { id: "subscriberInvoiceNumber", name: "Subscriber Invoice Number", selector: row => row.customerInvoiceNumber, width: '150px' },
        { id: "serviceProviderInvoiceStatus", name: "Service Provider Invoice Status", selector: row => row.vendorBillingStatus, width: '150px' },
        { id: "serviceProviderInvoiceNumber", name: "Service Provider Invoice Number", selector: row => row.vendorInvoiceNumber, width: '150px' },
        { id: "requestedDate", name: "Project Delivery Date", selector: row => row.requestedDate.date, width: '150px', sortable: true },
        { id: "department", name: "Department", selector: row => row.userGroupName, width: '150px' },
        { id: "projectOwner", name: "Project Owner", selector: row => row.projectOwner, width: '150px' },
        { id: "poNumber", name: "PO Number", selector: row => row.poNumber, width: '150px' },
        { id: "userType", name: "Type", selector: row => humanize(row.userType), width: '150px' }
    ], []);

    const closeRightModal = () => {
        setShowAdvanceFilter(false);
    }

    const noDataFoundComponent = useMemo(() => (
        <div role="rowgroup">
          <div role="row" className="no-data-display align-center">
            <div role="cell">
              <i className="nodata-icon"></i>
              <p>{Constants.NO_DATA_FOUND}</p>
            </div>
          </div>
        </div>
    ), []);

    function removeFilter(removableLabel) {

        if (removableLabel === 'Service_Provider') {
          delete searchAdFilters.Service_Provider;
        }
        if (removableLabel === 'Project_Status') {
          delete searchAdFilters.Project_Status;
        }
        if (removableLabel === 'Subscriber_Invoice_Status') {
          delete searchAdFilters.Subscriber_Invoice_Status;
        }
        if (removableLabel === 'Project_ID') {
          delete searchAdFilters.Project_ID;
        }
        if (removableLabel === 'Service_Provider_Invoice_Status') {
          delete searchAdFilters.Service_Provider_Invoice_Status;
        }
        if (removableLabel === 'Departments') {
            delete searchAdFilters.Departments;
        }
        if (removableLabel === 'Project_Owner') {
            delete searchAdFilters.Project_Owner;
        }
        if (removableLabel === 'Po_Number') {
            delete searchAdFilters.Po_Number;
        }
        if (removableLabel === 'Subscriber_Invoice_Number') {
            delete searchAdFilters.Subscriber_Invoice_Number;
        }
        if (removableLabel === 'Service_Provider_Invoice_Number') {
            delete searchAdFilters.Service_Provider_Invoice_Number;
        }
        const newFilters = { ...searchAdFilters };
    
        updateSearchAdFilters(newFilters);
    }

    const handleSort = async (column, sortDirection) => {
      var order = sortDirection.toUpperCase();
      var sortedColumn = column.id;
      var sortedString = `sortOrder=${order}&sortColumn=${sortedColumn}`;
      setSortParam(sortedString);
    };

    const downloadReport = () => {
      const filters = { accountNameFilter, dateFilter, searchAdFilters };
      const queryParams = buildQueryParams(filters);
      const queryString = queryParams.toString();
      downloadFile(`${APIUrls.REPORT_LIST_FINANCE_EXPORT}?${queryString}`, 'Finance_Report.xlsx')
          .catch((err) => {
              handleError(err);
          });
    }

    return <div className="body bg-color">
    <div id="container" className="dasboard-wrapper">
        <Header />
        <LeftNavbar />
      <section className="dashboard-content">
        <div id="content" className="dashboard-wrap campaign-section">
          <FinanceFilter retainFilter={retainFilter} showProjectNameFilter={false} showDownloadReport={true} setShowAdvanceFilter={setShowAdvanceFilter}
            setDateFilter={setDateFilter} setAccountNameFilter={setAccountNameFilter} downloadReport={downloadReport}/>
          <div className="wrap-data listing-wrap-data">
              <div className="white-bg">
                <div className="campaign-hedaing flex justify-center">
                    <h3 className="font-weight500">Reports</h3>
                </div>
                <div className="campaign-hedaing flex justify-center">
                  <ul className="selected-data flex">
                    {Object.entries(searchAdFilters).map(([label, value]) => {
                        if (value.label?.length !== 0 && value.label !== Constants.SELECT) {
                            return (
                            <li key={label}>
                                <button onClick={(e) => { removeFilter(label) }}>
                                <p aria-label={`Remove : ${value.label}`} >
                                    <small>{label.replaceAll("_", " ")} : </small> {label !== "Project_ID" ? value.label : value}
                                </p>
                                <span>
                                    {AdvanceSearchParamsIcon}
                                </span>
                                </button>
                            </li>
                            );
                        }
                    })}
                  </ul>
                </div>
  
                <div className="datatable-block horizontal-overflow">
                    <DataTable 
                        columns={columns}
                        data={projects}
                        striped={true}
                        sortServer
                        onSort={handleSort}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationRowsPerPageOptions={[10, 20, 40, 50, 100, 200]}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        noDataComponent={noDataFoundComponent}
                    />
                </div>
  
              </div>
          </div>
  
        </div>
      </section>
      <div className="overlay" onClick={closeRightModal}></div>
      <ConditionalBlock condition={showAdvanceFilter}>
        <FinanceAdvanceFilter setShowAdvanceFilter={setShowAdvanceFilter} updateSearchAdFilters={updateSearchAdFilters} searchAdFilters={searchAdFilters} reportsAdvanceFilter={true} accountId={accountNameFilter} />
      </ConditionalBlock>
    </div>
    
    <div className={globalOverlay ? "gb-overlay show" : "gb-overlay hide"}>
        <div className="gb-loader">
          <RotatingLines strokeColor="#0096FF" strokeWidth="5" width="196" visible={true} />
        </div>
    </div>
  </div>
}
